// "Copy from" popover: source day · scope · merge/replace · whole-week toggle. Session
// scopes keep their wire values ("am" / "pm"); only the words come from the configured labels.
import { useState } from "react";
import { Copy } from "lucide-react";
import { cn } from "@/lib/utils";
import { sessionLabelFor } from "@/lib/work-plan-sessions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PopoverContent } from "@/components/ui/popover";
import { formatDayLabel, shiftDate } from "./work-plan-utils";

function RadioRow({ selected, onSelect, children, className }) {
  return (
    <button
      type="button"
      className={cn(
        "flex w-full items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left text-xs transition-colors",
        selected
          ? "border-primary bg-primary/5 font-semibold text-primary"
          : "border-border bg-card hover:bg-muted/50",
        className,
      )}
      onClick={onSelect}
    >
      <span
        className={cn(
          "size-3.5 shrink-0 rounded-full border-[1.5px]",
          selected ? "border-primary bg-primary shadow-[inset_0_0_0_2.5px_var(--card)]" : "border-muted-foreground/40",
        )}
      />
      {children}
    </button>
  );
}

function SectionLabel({ children }) {
  return <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{children}</p>;
}

export default function CopyPopoverContent({ date, clinics, sessions, busy, onCopy }) {
  const previousDay = shiftDate(date, -1);
  const lastWeek = shiftDate(date, -7);
  const [source, setSource] = useState(previousDay);
  const [scope, setScope] = useState("day");
  const [scopeTenantId, setScopeTenantId] = useState(clinics[0]?.id || "");
  const [mode, setMode] = useState("merge");
  const [weekly, setWeekly] = useState(false);
  const amLabel = sessionLabelFor(sessions, "am");
  const pmLabel = sessionLabelFor(sessions, "pm");

  return (
    <PopoverContent align="end" className="w-[330px] p-3">
      <SectionLabel>Copy from</SectionLabel>
      <div className="flex flex-col gap-1.5">
        <RadioRow selected={source === previousDay} onSelect={() => setSource(previousDay)}>
          Last working day · {formatDayLabel(previousDay)}
        </RadioRow>
        <RadioRow selected={source === lastWeek} onSelect={() => setSource(lastWeek)}>
          Same weekday last week · {formatDayLabel(lastWeek)}
        </RadioRow>
        <label className="mt-0.5 flex flex-col gap-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          Or pick a date
          <Input
            type="date"
            value={source}
            onChange={(event) => setSource(event.target.value)}
            className="h-8 text-xs font-normal normal-case tracking-normal"
          />
        </label>
      </div>

      <div className="my-3 h-px bg-border" />
      <SectionLabel>What to copy</SectionLabel>
      <div className="flex flex-col gap-1.5">
        <RadioRow selected={scope === "day"} onSelect={() => setScope("day")}>
          Whole day — all clinics, {amLabel} + {pmLabel}
        </RadioRow>
        <RadioRow selected={scope === "clinic"} onSelect={() => setScope("clinic")}>
          <span className="flex min-w-0 flex-1 items-center gap-2">
            Only
            <select
              value={scopeTenantId}
              onChange={(event) => {
                setScopeTenantId(event.target.value);
                setScope("clinic");
              }}
              onClick={(event) => event.stopPropagation()}
              className="min-w-0 flex-1 truncate rounded-md border border-border bg-card px-1.5 py-0.5 text-xs font-normal text-foreground outline-none"
            >
              {clinics.map((clinic) => (
                <option key={clinic.id} value={clinic.id}>
                  {clinic.name}
                </option>
              ))}
            </select>
          </span>
        </RadioRow>
        <RadioRow selected={scope === "am"} onSelect={() => setScope("am")}>
          {amLabel} session only
        </RadioRow>
        <RadioRow selected={scope === "pm"} onSelect={() => setScope("pm")}>
          {pmLabel} session only
        </RadioRow>
      </div>

      <div className="my-3 h-px bg-border" />
      <div className="flex gap-1.5">
        <RadioRow className="flex-1" selected={mode === "merge"} onSelect={() => setMode("merge")}>
          Merge
        </RadioRow>
        <RadioRow className="flex-1" selected={mode === "replace"} onSelect={() => setMode("replace")}>
          Replace
        </RadioRow>
      </div>
      <label className="mt-2.5 flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
        <input
          type="checkbox"
          checked={weekly}
          onChange={(event) => setWeekly(event.target.checked)}
          className="size-3.5 accent-primary"
        />
        Apply to the whole week (Mon–Sun) instead of one day
      </label>

      <Button
        type="button"
        size="sm"
        className="mt-3 w-full [&_svg]:size-3.5"
        disabled={busy || !source || (scope === "clinic" && !scopeTenantId)}
        onClick={() =>
          onCopy({
            fromDate: source,
            scope,
            tenantId: scope === "clinic" ? scopeTenantId : undefined,
            mode,
            weekly,
          })
        }
      >
        <Copy />
        Copy into {weekly ? "this week" : formatDayLabel(date)}
      </Button>
    </PopoverContent>
  );
}
